import { z } from "zod";

/**
 * Shared DTO cho Admin bulk member-import (US-AE-IMP2-T14 · @UC9162 member-import).
 * MỘT NGUỒN SỰ THẬT cho api (staging/commit) + admin web (upload + preview + confirm).
 *
 * Use-case: nhóm gửi Excel/CSV (tên nhóm + user zalo/phone/email) → admin upload → PREVIEW validate
 * per-row → CONFIRM tạo group+fund+user atomic (rollback nếu lỗi) → user login được (Zalo OAuth /
 * email-OTP theo kênh). Phone-only chưa login được (no-SMS C1) — báo skip login rõ ràng.
 *
 * BẤT BIẾN (invariants):
 *  - PII (C2): zalo/email/phone KHÔNG log raw. DTO chỉ mang giá trị đã chuẩn hoá để hiển thị preview.
 *  - Formula-injection: groupName/userName neutralize (domain) TRƯỚC khi vào DTO.
 *  - Atomic: previewToken opaque tham chiếu batch đã parse — commit theo token (idempotent).
 *  - KHÔNG field tiền (import tạo shell group+fund, KHÔNG khởi tạo số dư/bút toán — G2 ledger tách).
 *  - `.strict()` mọi object đọc — chống field lạ trên wire.
 */

// ============================================================
// @UC9162 — Preview (dry-run parse + validate)
// ============================================================

/** Một dòng đã validate cho preview (KHÔNG chứa định danh raw đầy đủ — chỉ đủ để admin đối chiếu). */
export const adminImportPreviewRowSchema = z
  .object({
    /** Số dòng gốc trong file (1-based, KHÔNG tính header). */
    line: z.number().int().positive(),
    groupName: z.string(),
    userName: z.string(),
    /** Định danh đã chuẩn hoá (hiển thị preview; PII — client KHÔNG log). null nếu vắng/không hợp lệ. */
    zaloUid: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    valid: z.boolean(),
    errors: z.array(z.string()),
  })
  .strict();
export type AdminImportPreviewRow = z.infer<typeof adminImportPreviewRowSchema>;

/**
 * Kết quả preview upload (dry-run). previewToken tham chiếu batch staging đã parse (TTL ngắn) để
 * commit sau. hasErrors=true ⇒ FE chặn commit (atomic — không import nếu bất kỳ dòng lỗi).
 */
export const adminImportPreviewResponseSchema = z
  .object({
    previewToken: z.string(),
    totalRows: z.number().int().nonnegative(),
    validRows: z.number().int().nonnegative(),
    invalidRows: z.number().int().nonnegative(),
    /** Số nhóm phân biệt sẽ được tạo/gộp (theo groupName). */
    groupCount: z.number().int().nonnegative(),
    hasErrors: z.boolean(),
    rows: z.array(adminImportPreviewRowSchema),
  })
  .strict();
export type AdminImportPreviewResponse = z.infer<typeof adminImportPreviewResponseSchema>;

// ============================================================
// @UC9162 — Commit (atomic create group+fund+user)
// ============================================================

/** Kênh login khả dụng cho user sau import (theo định danh). phone_only ⇒ chưa login được (no-SMS). */
export const importLoginChannelSchema = z.enum(["zalo", "email_otp", "phone_no_channel"]);
export type ImportLoginChannel = z.infer<typeof importLoginChannelSchema>;

/** Body commit — xác nhận import theo previewToken (idempotent theo token). */
export const adminImportCommitRequestSchema = z
  .object({
    previewToken: z.string().min(1),
  })
  .strict();
export type AdminImportCommitRequest = z.infer<typeof adminImportCommitRequestSchema>;

/** Tóm tắt 1 nhóm được tạo/gộp trong commit. */
export const adminImportGroupResultSchema = z
  .object({
    groupId: z.string(),
    groupName: z.string(),
    fundId: z.string(),
    /** true nếu group được tạo mới trong commit này; false nếu gộp vào group đã tồn tại (dedup theo tên). */
    created: z.boolean(),
    memberCount: z.number().int().nonnegative(),
  })
  .strict();
export type AdminImportGroupResult = z.infer<typeof adminImportGroupResultSchema>;

/** Tóm tắt 1 user được tạo/link trong commit + kênh login. */
export const adminImportUserResultSchema = z
  .object({
    line: z.number().int().positive(),
    userId: z.string(),
    userName: z.string(),
    /** true nếu user tạo mới; false nếu dedup vào user đã tồn tại (theo định danh). */
    created: z.boolean(),
    /** Kênh login user có thể dùng (zalo/email_otp) hoặc phone_no_channel (chưa login được). */
    loginChannels: z.array(importLoginChannelSchema),
  })
  .strict();
export type AdminImportUserResult = z.infer<typeof adminImportUserResultSchema>;

/**
 * Kết quả commit atomic. Tất-cả-hoặc-không (rollback nếu lỗi). usersLoginable = số user có ≥1 kênh
 * login thật (zalo/email_otp); usersNoLoginChannel = phone-only (cần bổ sung phone-OTP để login).
 */
export const adminImportCommitResponseSchema = z
  .object({
    groups: z.array(adminImportGroupResultSchema),
    users: z.array(adminImportUserResultSchema),
    groupsCreated: z.number().int().nonnegative(),
    usersCreated: z.number().int().nonnegative(),
    usersLinked: z.number().int().nonnegative(),
    usersLoginable: z.number().int().nonnegative(),
    usersNoLoginChannel: z.number().int().nonnegative(),
  })
  .strict();
export type AdminImportCommitResponse = z.infer<typeof adminImportCommitResponseSchema>;
